import React, { useState } from "react";
import { Box, Tooltip, useTheme } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { colors } from "../../../styles/theme";
import { mapIcons } from "../../../assets/mapIcons";

interface LayerControlProps {
  layerVisibility: {
    windenergieanlagen: boolean;
    land_parcels: boolean;
  };
  onToggleLayer: (
    layerName: keyof LayerControlProps["layerVisibility"],
  ) => void;
  isFullscreen?: boolean;
}

const DockButton: React.FC<{
  icon: string;
  label: string;
  active: boolean;
  color: string;
  onClick: () => void;
  size?: number;
  isFullscreen?: boolean;
  disabled?: boolean;
}> = ({
  icon,
  label,
  active,
  color,
  onClick,
  size = 48,
  isFullscreen = true,
  disabled = false,
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      whileHover={{ scale: 1.2 }}
      whileTap={{ scale: 0.95 }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 25,
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <Tooltip
        title={label}
        placement={isFullscreen ? "top" : "right"}
        arrow
        slotProps={{
          popper: {
            sx: { zIndex: theme.zIndex.modal - 100 },
          },
        }}
      >
        <Box
          onClick={disabled ? undefined : onClick}
          sx={{
            width: size,
            height: size,
            borderRadius: "50%",
            backgroundColor: disabled
              ? colors.state.disabledLayer
              : active
                ? `rgba(${color}, 0.9)`
                : colors.glass.backgroundSemiSolid,
            backdropFilter: "blur(10px)",
            border: disabled
              ? "2px solid rgba(200, 200, 200, 0.5)"
              : active
                ? `2px solid ${color}`
                : `2px solid ${colors.glass.borderStrong}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: disabled ? "not-allowed" : "pointer",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: disabled
              ? "0 2px 8px rgba(0, 0, 0, 0.1)"
              : active
                ? `0 8px 32px ${color}40, 0 4px 16px rgba(0, 0, 0, 0.1)`
                : "0 4px 16px rgba(0, 0, 0, 0.1)",
            "&:hover": {
              boxShadow: disabled
                ? "0 2px 8px rgba(0, 0, 0, 0.1)"
                : active
                  ? `0 12px 40px ${color}50, 0 8px 24px rgba(0, 0, 0, 0.15)`
                  : "0 8px 24px rgba(0, 0, 0, 0.15)",
            },
          }}
        >
          <img
            src={icon}
            alt={label}
            style={{
              width: isHovered ? size * 0.7 : size * 0.6,
              height: isHovered ? size * 0.7 : size * 0.6,
              filter: disabled
                ? "grayscale(100%) brightness(0.5)"
                : active
                  ? "none"
                  : "grayscale(100%) brightness(0.8)",
              transition: "all 0.3s ease",
              userSelect: "none",
              objectFit: "contain",
            }}
          />
        </Box>
      </Tooltip>
    </motion.div>
  );
};

const LayerControl: React.FC<LayerControlProps> = ({
  layerVisibility,
  onToggleLayer,
  isFullscreen = true,
}) => {
  const layerConfig: Array<{
    key: keyof LayerControlProps["layerVisibility"];
    icon: string;
    label: string;
    color: string;
    active: boolean;
    disabled?: boolean;
  }> = [
    {
      key: "windenergieanlagen",
      icon: mapIcons.windrad,
      label: "Windenergieanlagen",
      color: colors.map.plannedWindTurbines,
      active: layerVisibility.windenergieanlagen,
    },
    {
      key: "land_parcels",
      icon: mapIcons.flurstueck,
      label: "Flurstücke",
      color: colors.map.landParcels,
      active: layerVisibility.land_parcels,
    },
  ];

  return (
    <Box
      sx={{
        position: "relative",
        zIndex: 1000,
      }}
    >
      <motion.div
        initial={{ opacity: 0, [isFullscreen ? "y" : "x"]: 20 }}
        animate={{ opacity: 1, [isFullscreen ? "y" : "x"]: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <Box
          sx={{
            backgroundColor: colors.glass.backgroundLayer,
            backdropFilter: "blur(24px) saturate(180%)",
            border: `1px solid ${colors.glass.borderLayer}`,
            borderRadius: isFullscreen ? "28px" : "24px",
            px: isFullscreen ? 2 : 2,
            py: isFullscreen ? 1 : 2,
            boxShadow:
              "0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 32px rgba(0, 0, 0, 0.1)",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: isFullscreen ? "28px" : "24px",
              padding: "1px",
              background: `linear-gradient(135deg, ${colors.glass.borderStrong}, ${colors.glass.border})`,
              WebkitMask:
                "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: isFullscreen ? "row" : "column",
              gap: isFullscreen ? 3 : 2,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {layerConfig.map((layer, index) => (
              <motion.div
                key={layer.key}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.4,
                  delay: index * 0.1,
                  ease: "easeOut",
                }}
              >
                <DockButton
                  icon={layer.icon}
                  label={layer.label}
                  active={layer.active}
                  color={layer.color}
                  onClick={() => onToggleLayer(layer.key)}
                  size={isFullscreen ? 48 : 40}
                  isFullscreen={isFullscreen}
                  disabled={layer.disabled || false}
                />
              </motion.div>
            ))}
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
};

export default LayerControl;
